import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useAuthStore } from '../../stores/AuthStore/AuthStore';
import styleVariables from '../../scss/_variables.scss';
import { getCurrentVersion } from 'src/utils/version';
import styles from './CustomAppBar.module.scss';
import MenuIcon from '@mui/icons-material/Menu';
import { BOT_NAME } from 'src/constants/Bot';
// import NotificationMenu from '../notification-menu/NotificationMenu';
import ErrorBoundary from '../error-boundary/ErrorBoundry';
import { USER_DETAILS_NOT_FOUND_ERROR } from 'src/constants/errors';
import ProfileModal from '../profile-modal/ProfileModal';
import GOOGLE_CHAT_LOGO from '../../assets/google-chat-icon.png';
import { getGoogleChatHomeUrl } from 'src/utils/googleChat';
import { CONFIG } from 'src/constants/config';
import OrgSelector from '../org-selector/OrgSelector';
import ActiveUsersAvatarGroup from '../active-users/ActiveUsersAvatarGroup';
import { useActiveUsersStore } from 'src/stores/ActiveUsersStore/ActiveUsersStore';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/constants/navigateRoutes';
import { useTranslation } from 'react-i18next';
import GlobalSearch from '../global-search/GlobalSearch';

export interface CustomAppBarProps {
  setIsSideNavBarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomAppBar = observer(({ setIsSideNavBarOpen }: CustomAppBarProps) => {
  const { getUserDetails, isRevampExperience } = useAuthStore();
  const userDetails = getUserDetails();
  const activeUsersStore = useActiveUsersStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const revampExperience = isRevampExperience();

  useEffect(() => {
    activeUsersStore.startPolling();

    return () => activeUsersStore.stopPolling();
  }, [activeUsersStore]);

  if (!userDetails) {
    return <ErrorBoundary message={USER_DETAILS_NOT_FOUND_ERROR} />;
  }

  const openGoogleChat = () => {
    window.open(
      getGoogleChatHomeUrl(userDetails.email),
      '_blank',
      'noopener,noreferrer',
    );
  };

  if (revampExperience) {
    return (
      <AppBar position="static" elevation={0}>
        <Toolbar
          id="app-bar-header"
          sx={{
            minHeight: 56,
            px: { xs: 1.5, md: 2.5 },
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1.5,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setIsSideNavBarOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Box id="org-selector-tour-anchor" sx={{ minWidth: 0 }}>
              <OrgSelector revamp />
            </Box>
            <Box sx={{ height: 20, borderLeft: 1, borderColor: 'divider' }} />
            <GlobalSearch />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              className={styles.activeUsersAvatarGroup}
              sx={{ display: { xs: 'none', lg: 'flex' } }}
            >
              <ActiveUsersAvatarGroup />
            </Box>
            <Button
              aria-label="Open Hana in Google Chat"
              id="open-google-chat-app-bar"
              variant="outlined"
              onClick={openGoogleChat}
              startIcon={
                <img
                  className={styles['google-chat-logo']}
                  src={GOOGLE_CHAT_LOGO}
                  alt=""
                />
              }
              endIcon={<OpenInNewRoundedIcon fontSize="small" />}
              sx={{
                display: 'inline-flex',
                minWidth: { xs: 40, sm: 64 },
                px: { xs: 1, sm: 1.5 },
                color: 'text.primary',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 1 },
                },
                '& .MuiButton-endIcon': {
                  display: { xs: 'none', lg: 'inherit' },
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'inline' } }}
              >
                {t('revamp_shell.open_chat', { defaultValue: 'Open Chat' })}
              </Box>
            </Button>
            <IconButton
              aria-label={t('revamp_shell.open_help', {
                defaultValue: 'Open help',
              })}
              onClick={() => navigate(PATHS.help)}
              sx={{
                color: 'text.secondary',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              <HelpOutlineRoundedIcon />
            </IconButton>
            <ProfileModal
              clickId="navbar_user_profile_popover"
              buttonId="navbar_user_profile_btn"
              revamp
            />
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <AppBar
      position="static"
      elevation={0} // ← removes MUI's default shadow
      sx={{
        boxShadow: 'none', // ← extra precaution to ensure no shadow
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          background: styleVariables.chineseBlack,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={() => setIsSideNavBarOpen(true)}
                sx={{
                  display: { sx: 'block', md: 'none' },
                  padding: 0,
                  margin: 0,
                }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  className={styles['app-bar-info-text']}
                  id="app-bar-header"
                >
                  <Typography
                    sx={{ fontSize: { xs: '16px', lg: '24px' } }}
                    variant="h5"
                    component="h1"
                  >
                    {BOT_NAME} Control Dashboard
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', lg: '16px' },
                      lineHeight: '24px',
                      color: styleVariables.mutedGray,
                      textTransform: 'capitalize',
                    }}
                    variant="h6"
                  >
                    {CONFIG.ENVIRONMENT ?? ''} Version: {getCurrentVersion()}
                  </Typography>
                </div>
              </Box>
              <Box className={styles.headerUtilityActions}>
                <Button
                  aria-label="open Google Chat"
                  id="open-google-chat-app-bar"
                  variant="text"
                  className={styles['google-chat-logo-button']}
                  onClick={openGoogleChat}
                >
                  <Box className={styles['google-chat-logo-iconShell']}>
                    <img
                      className={styles['google-chat-logo']}
                      src={GOOGLE_CHAT_LOGO}
                      alt="Google Chat logo"
                    />
                  </Box>
                  <Box className={styles['google-chat-copy']}>
                    <Typography
                      variant="body1"
                      className={styles['google-chat-logo-text']}
                    >
                      Google Chat
                    </Typography>
                  </Box>
                  <OpenInNewRoundedIcon
                    className={styles['google-chat-logo-launchIcon']}
                    fontSize="small"
                  />
                </Button>
                <Box
                  className={styles.orgSelector}
                  id="org-selector-tour-anchor"
                >
                  <OrgSelector />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: {
                  xs: '4px',
                  md: '16px',
                },
                alignItems: 'center',
              }}
            >
              <Box className={styles.activeUsersAvatarGroup}>
                <ActiveUsersAvatarGroup />
              </Box>
              {/* Keep the notification menu code path intact for future work,
                  but do not render it until the notification engine is ready. */}
              {/* <NotificationMenu notifications={[]} userRole={userDetails.role} /> */}
              <ProfileModal
                clickId="navbar_user_profile_popover"
                buttonId="navbar_user_profile_btn"
              />
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
});

export default CustomAppBar;
